/**
 * Presents a UI suited for editing permissions associated with a given producer.
 * ## Fields
 * Name | Description
 * --- | ---
 * group | Gets and sets the currently-selected `Group`.
 * @module app/views/admin/permissions/Editor
 */
import Backbone from "backbone";
import _ from "underscore";
import UserCollection from "app/models/UserCollection";
import Field from "core/models/Field";
import FormInputView from "core/views/FormInputView";
import Permission from "app/models/Permission";
import CollectionTableView from "core/views/CollectionTableView";
import User from "app/models/User";
import $ from "jquery";
import s from "underscore.string";
import ErrorUtils from "app/utils/ErrorUtils";
import session from "app/Session";
import messenger from "app/Messenger";
import _remove from "lodash.remove";
export default Backbone.View.extend({
  className: "permissions-editor",
  template: _.template(
    '<div class="group-container"></div><div class="message-container"><label class="message"></label></div><div class="action-container"></div><button class="btn btn-default toggle-users" type="button" data-toggle="collapse" data-target=".user-container">Show Users</button><div class="collapse user-container"></div>'
  ),
  /**
   * Initializes the interface.
   * @param  {object} options The options hash.
   * @param {Array} options.groups A set of `Group` models to place in the group selector.
   * @param {object} options.actions A mapping from action key to text specifying the actions which can be associated with the given producer.
   * @param {string|object} options.type Specifies the "producer" for the permissions in question. Either a model type or a mapping from model type to model id.
   * @param {Boolean} options.allowAllUsers:false True to indicate that an all users pseudogroup should be allowed for selection.
   * @param {Boolean} options.allowPublic:false True to indicate that setting "public" (non-consumer-specific) permissions should be allowed.
   */
  initialize: function (options) {
    this.options = _.defaults({}, options, {
      groups: [], //we need a collection of group models to put in the group selector
      actions: {} /*action key (e.g. "create") -> action display text (e.g. "Create an item.")*/,
      type: null, //Type of entity to add permissions for. either a string or a map from type to id. or null.
      allowAllUsers: false, //true to indicate that an all users pseudogroup should be allowed for selection.
      allowPublic: false, //true to indicate that setting "public" (non-consumer-specific) permissions should be allowed.
    });

    this._groupList = this._createGroupList();
    this._groupsById = {};

    _.each(
      this._groupList,
      function (group) {
        this._groupsById[group.id] = group;
      },
      this
    );

    this._selectedUsers = [];
    this._selectedGroup = _.first(this._groupList);

    Object.defineProperty(this, "group", {
      get: _.bind(function () {
        return this._selectedGroup;
      }, this),
      set: _.bind(function (value) {
        this._selectGroup(value);
        this.$("#group").val(value.id);
      }, this),
    });
  },

  events: {
    "click .toggle-users": function () {
      var btn = this.$(".toggle-users");

      if (btn.html() == "Show Users") {
        btn.html("Hide Users");
      } else {
        btn.html("Show Users");
      }
    },
  },

  _createGroupList: function () {
    var groups = this.options.groups;
    var result = groups.models ? groups.models.slice() : groups.slice();

    if (this.options.allowAllUsers) {
      var group;

      if (!session.hasRole("admin")) {
        //if user is not an admin, "All Users" should be all users in their organization
        group = _.detect(this.options.groups, function (group) {
          return (
            session.user.get("Organization").id == group.get("organization")
          );
        });
      }

      var AllUserModel = Backbone.Model.extend({
        defaults: {
          name: "All Users",
          id: "all",
        },
        getUsers: function (callback) {
          if (group) {
            group.getUsers(callback);
          } else {
            var users = new UserCollection([]);

            users.fetch({
              success: function (collection) {
                callback(null, collection);
              },
              error: function (collection, response, options) {
                callback(
                  ErrorUtils.handleApiError({}, collection, response, options)
                );
              },
            });
          }
        },
      });

      this._allUsers = new AllUserModel();
      result.unshift(this._allUsers);
    }

    if (this.options.allowPublic) {
      var PublicModel = Backbone.Model.extend({
        defaults: {
          name: "Global",
          id: "undefined",
        },
        getUsers: function (callback) {
          var users = new UserCollection([]);

          callback(null, users);
        },
      });

      this._public = new PublicModel();
      result.unshift(this._public);
    }

    return result;
  },

  _renderGroups: function () {
    var selectionModel = {
      group: this._selectedGroup.id,
    };

    var selectionField = new Field({
      id: "group",
      name: "group",
      label: "Group Selection",
      values: _.map(this._groupList, function (group) {
        return {
          value: group.id,
          text: group.get("name"),
        };
      }),
    });
    var selectionView = new FormInputView({
      field: selectionField,
      model: selectionModel,
    });

    selectionView.render();
    selectionView.on(
      "change",
      function (value) {
        this._selectGroup(this._groupsById[value]);
      },
      this
    );
    this.$(".group-container").html("").append(selectionView.$el);
  },

  //returns false if there is no selection
  _buildMessageAndConsumer: function () {
    var result = null;

    if (this._selectedUsers.length) {
      result = {
        consumer: this._selectedUsers,
        msgEnd: "the selected users.",
      };
    } else if (this._selectedGroup) {
      var consumer, msgEnd;

      switch (this._selectedGroup.id) {
        case "all":
          consumer = this._selectedGroup;
          msgEnd = "all users.";
          break;

        case "undefined":
          consumer = null;
          msgEnd = "all users.";
          break;
        default:
          consumer = this._selectedGroup;
          msgEnd = "the selected user group.";
          break;
      }

      result = {
        consumer: consumer,
        msgEnd: msgEnd,
      };
    }

    return result;
  },

  _addAction: function (action) {
    var actions = {};
    var msgConsumer = this._buildMessageAndConsumer();

    actions[action] = true;

    if (msgConsumer) {
      Permission.update(
        msgConsumer.consumer,
        this.options.type,
        actions,
        function (err) {
          if (err) {
            return messenger.error(err);
          }

          messenger.success(
            'Added "' + action + '" permission for ' + msgConsumer.msgEnd
          );
        }
      );
    }
  },

  _removeAction: function (action) {
    var actions = {};
    var msgConsumer = this._buildMessageAndConsumer();

    actions[action] = false;

    if (msgConsumer) {
      Permission.update(
        msgConsumer.consumer,
        this.options.type,
        actions,
        function (err) {
          if (err) {
            return messenger.error(err);
          }

          messenger.success(
            'Removed "' + action + '" permission for ' + msgConsumer.msgEnd
          );
        }
      );
    }
  },

  _destroyAction: function (action) {
    var actions = {};
    var msgConsumer = this._buildMessageAndConsumer();

    actions[action] = false;

    if (msgConsumer) {
      Permission.destroy(
        msgConsumer.consumer,
        this.options.type,
        action,
        function (err) {
          if (err) {
            return messenger.error(err);
          }

          messenger.success(
            'Reset "' + action + '" permission for ' + msgConsumer.msgEnd
          );
        }
      );
    }
  },

  _selectUser: function (user) {
    this._selectedUsers.push(user);
    this._setActions(this._selectedUsers);
    this._updateLayout();
  },

  _unselectUser: function (user) {
    _remove(this._selectedUsers, user);

    if (this._selectedUsers.length) {
      this._setActions(this._selectedUsers);
    } else if (this._selectedGroup) {
      this._setActions(this._selectedGroup);
    }

    this._updateLayout();
  },

  _isUserSelected: function (user) {
    return (
      user &&
      !!_.find(
        this._selectedUsers,
        function (selectedUser) {
          return user.id == selectedUser.id || user === selectedUser;
        },
        this
      )
    );
  },

  _renderActions: function () {
    var self = this;
    var actions = [];

    _.each(this.options.actions, function (description, action) {
      actions.push({
        name: action,
        allowed: "none",
        label: description,
      });
    });

    var fields = [
      new Field({
        id: "allowed",
        name: "allowed",
        label: "Allow",
        values: [
          {
            value: "none",
            text: "Default",
          },
          {
            value: "yes",
            text: "Yes",
          },
          {
            value: "no",
            text: "No",
          },
        ],
        sortable: false,
        filterable: false,
      }),
      new Field({
        id: "label",
        name: "label",
        label: "Action",
        sortable: false,
        filterable: false,
      }),
    ];

    var actionView = new CollectionTableView({
      allowSave: false,
      allowDelete: false,
      mode: "edit",
      fields: fields,
      collection: actions,
    });

    //todo subscribe to row-add to automatically update row IDs

    actionView.collection.on(
      "change:allowed",
      function (model) {
        var action = model.get("name");

        switch (model.get("allowed")) {
          case "none":
            self._destroyAction(action);
            break;

          case "yes":
            self._addAction(action);
            break;

          case "no":
            self._removeAction(action);
            break;
        }
      },
      this
    );

    actionView.render();
    actionView.$el.addClass("action-list");
    _.each(actionView.rows, function (row) {
      var label = row.model.get("label"),
        action = row.model.get("name");

      row.$el.attr("id", s.classify(label));
      row
        .$("select")
        .addClass("js-allow-action")
        .attr("id", "action-" + s.slugify(action));
    });
    this.$(".action-container").html("").append(actionView.$el);
  },

  _renderUsers: function (users) {
    var self = this;
    var fields = [
      new Field({
        id: "selected",
        label: "Select",
        name: "selected",

        editor: "custom",
        ViewerView: {
          tagName: "input",
          attributes: {
            type: "checkbox",
          },
          className: "js-include-user",

          template: function () {
            return "";
          },

          events: {
            click: function () {
              this.setValue(this.el.checked);
            },
          },

          getControlValue: function () {
            return self._isUserSelected(this.options.model);
          },

          setControlValue: function (value) {
            if (value) {
              self._selectUser(this.options.model);
            } else {
              self._unselectUser(this.options.model);
            }
          },

          postRender: function () {
            this.el.checked = this.getControlValue();
          },
        },
      }),
      new Field(User.fields["Username"]).decorate({
        editable: false,
      }),
      new Field(User.fields["Name"]).decorate({
        editable: false,
        editor: "text",
        name: "fullName",
        id: "fullName",
      }),
    ];

    var userView = new CollectionTableView({
      allowSave: false,
      allowDelete: false,
      mode: "view",
      fields: fields,
      collection: users,
    });

    userView.render();
    userView.$el.addClass("user-list");
    this.$(".user-container").html("").append(userView.$el);
  },

  _selectGroup: function (group) {
    var self = this;

    if (group) {
      group.getUsers(function (err, users) {
        if (err) {
          return ErrorUtils.handleError({}, err);
        }

        self._selectedUsers = [];
        self._selectedGroup = group;
        self._updateLayout();
        self._renderUsers(users);
        self._setActions(group);
      });
    } else {
      self._selectedUsers = [];
      self._selectedGroup = null;
      self._updateLayout();
      self._renderUsers([]);
      self._clearActions();
    }
  },

  _setActions: function (consumers) {
    var INDETERMINATE = 2;
    var self = this;

    if (!_.isArray(consumers)) {
      consumers = [consumers];
    }

    this._clearActions();
    Permission.clear(consumers);
    Permission.find(consumers, this.options.type, function (err, results) {
      var actions = {};

      _.each(_.values(results), function (permHash) {
        _.each(permHash, function (value, action) {
          if (_.isBoolean(actions[action])) {
            if (actions[action] != value) {
              actions[action] = INDETERMINATE;
            }
          } else {
            actions[action] = value;
          }
        });
      });
      _.each(actions, function (value, action) {
        var selector = self.$("#action-" + s.slugify(action));

        if (selector) {
          if (!value) {
            selector.val("no");
          } else {
            if (value === INDETERMINATE) {
              selector.val("none");
            } else {
              selector.val("yes");
            }
          }
        }
      });
    });
  },

  _clearActions: function () {
    this.$(".js-allow-action").each(function () {
      $(this).val("none");
    });
  },

  _setMessage: function (text) {
    this.$(".message").html(text);
  },

  _updateLayout: function () {
    var editingGroup = true;

    if (this._selectedUsers.length) {
      editingGroup = false;
    }

    var singleGroup = false;

    if (this.options.groups.length <= 1) {
      singleGroup = true;
    }

    var allSelected = false;

    if (this._selectedGroup && this._selectedGroup.id == "all") {
      allSelected = true;
    }

    var publicSelected = false;

    if (this._selectedGroup && this._selectedGroup.id == "undefined") {
      publicSelected = true;
    }

    var msg;

    if (allSelected) {
      if (editingGroup) {
        this.$(".action-container").hide();
        msg = "Select users to modify their permissions.";
      } else {
        this.$(".action-container").show();
        msg = "Permissions for the selected users.";
      }
    } else {
      this.$(".action-container").show();

      if (publicSelected) {
        this.$(".user-container").hide();
        this.$(".toggle-users").hide();
        msg = "Public permissions.";
      } else {
        this.$(".user-container").show();
        this.$(".toggle-users").show();

        if (singleGroup) {
          this.$(".group-container").hide();
          msg = "Permissions for everyone.";

          if (!editingGroup) {
            msg = "Permissions for the selected users.";
          }
        } else {
          this.$(".group-container").show();
          msg =
            'Permissions for all users in "' +
            this._selectedGroup.get("name") +
            '" group.';

          if (!editingGroup) {
            msg = "Permissions for the selected users.";
          }
        }
      }
    }

    this._setMessage(msg);
  },

  render: function () {
    this.$el.html(this.template(this.options));
    this._renderGroups();
    this._renderActions();
    this._selectGroup(_.first(this._groupList));
  },
});



//////////////////
// WEBPACK FOOTER
// ./public/js/app/views/admin/permissions/Editor.js
// module id = 127
// module chunks = 1